import {
  Button,
  CounterBadge,
  makeStyles,
  SearchBox,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import type { ReactNode } from 'react';
import { FormGrid } from '#src/shared/ui/FormGrid';

const useStyles = makeStyles({
  bar: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  search: { flex: 1, maxWidth: 'none' },
});

export interface FilterBarProps {
  /** Nome da área de busca para leitores de tela (`Filtros das caixas`). */
  readonly label: string;
  readonly search: string;
  onSearch(text: string): void;
  readonly searchPlaceholder: string;
  /** Filtros em uso, contando a busca; acima de zero aparece "Limpar". */
  readonly activeCount: number;
  onClear(): void;
  /** Quantos filtros por linha na tela larga. */
  readonly columns?: 2 | 3 | 4;
  /** Os campos de filtro (`SelectField`, `AsyncCombobox`...). */
  readonly children: ReactNode;
}

/**
 * Barra de filtros das listas: a busca livre em destaque, os filtros numa
 * grade logo abaixo, e um "Limpar filtros" com a contagem do que está em uso.
 */
export function FilterBar({
  label,
  search,
  onSearch,
  searchPlaceholder,
  activeCount,
  onClear,
  columns = 4,
  children,
}: FilterBarProps) {
  const styles = useStyles();
  return (
    <search aria-label={label} className={styles.bar}>
      <div className={styles.searchRow}>
        <SearchBox
          className={styles.search}
          placeholder={searchPlaceholder}
          value={search}
          onChange={(_, data) => onSearch(data.value)}
        />
        {activeCount > 0 && (
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={onClear}
          >
            Limpar filtros{' '}
            <CounterBadge
              count={activeCount}
              size="small"
              color="informative"
            />
          </Button>
        )}
      </div>
      <FormGrid columns={columns}>{children}</FormGrid>
    </search>
  );
}
